import { gql } from '@apollo/client';
import IconOverflow from '@apollo/icons/default/IconOverflow.svg';
import IconTime from '@apollo/icons/default/IconTime.svg';
import IconServices from '@apollo/icons/small/IconServices.svg';
import IconSingleService from '@apollo/icons/small/IconSingleService.svg';
import {
  Alert,
  Avatar,
  AvatarGroup,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardHeaderControl,
  Group,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Text,
} from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import { uniqBy } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Link as ReactRouterLink,
  useHistory,
  useLocation,
} from 'react-router-dom';

import {
  API_SCHEMA_ID,
  SUPERGRAPH_SCHEMA_ID,
} from 'src/app/graph/documentationPage/sdlPage/sdlDisplay/SDLDisplay';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import {
  proposalChangesRouteConfig,
  proposalHomepageRouteConfig,
} from 'src/app/graph/routes';
import { PlainHtmlButton } from 'src/components/common/plainHtmlButton/PlainHtmlButton';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  LocalTimestamp,
  RelativeTimestamp,
} from 'src/components/timestamp/Timestamp';
import { useIdentity } from 'src/hooks/useIdentity';
import { useRouteParams } from 'src/hooks/useRouteParams';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { CommentStatus } from 'src/lib/graphqlTypes/types';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { setOverlayVisibleLocation } from 'src/lib/routing';
import { scrollTo } from 'src/lib/scrollTo';

import { CommentMarkdownEditor } from '../../CommentMarkdownEditor/CommentMarkdownEditor';
import { useGetUsersForMentions } from '../../CommentMarkdownEditor/useMentionUsers';
import { NEW_COMMENT_TEMP_ID } from '../../proposalCommentDrawer/shared';
import {
  SingleProposalComment,
  SingleProposalCommentFragment,
} from '../../proposalCommentDrawer/SingleProposalComment';
import { useAddProposalComment } from '../../proposalCommentDrawer/useAddProposalComment';
import { useEditProposalComment } from '../../proposalCommentDrawer/useEditProposalComment';

const MAX_VISIBLE_HOMEPAGE_COMMENTS = 3;

export const ActivityFeedCommentThreadChangeProposalCommentFragment = gql`
  fragment ActivityFeedCommentThreadChangeProposalCommentFragment on ParentChangeProposalComment {
    id
    schemaCoordinate
    schemaScope @include(if: $canQueryLaunches)
    ...SingleProposalCommentFragment
    replyCount
    replies {
      id
      ...SingleProposalCommentFragment
    }
    outdated
  }
  ${SingleProposalCommentFragment}
`;

export const ActivityFeedCommentThreadGeneralProposalCommentFragment = gql`
  fragment ActivityFeedCommentThreadGeneralProposalCommentFragment on ParentGeneralProposalComment {
    id
    ...SingleProposalCommentFragment
    replyCount
    replies {
      id
      ...SingleProposalCommentFragment
    }
  }
  ${SingleProposalCommentFragment}
`;

export const ActivityFeedCommentThreadReviewCommentFragment = gql`
  fragment ActivityFeedCommentThreadReviewCommentFragment on ReviewProposalComment {
    id
    ...SingleProposalCommentFragment
  }
  ${SingleProposalCommentFragment}
`;

export const ActivityFeedCommentThread = ({
  comment,
  graphRef,
  proposal,
  className,
  usersForMentions,
}: {
  comment:
    | GraphQLTypes.ActivityFeedCommentThreadChangeProposalCommentFragment
    | GraphQLTypes.ActivityFeedCommentThreadGeneralProposalCommentFragment
    | GraphQLTypes.ActivityFeedCommentThreadReviewCommentFragment;
  graphRef: GraphRef;
  className?: string;
  proposal: GraphQLTypes.UseAddCommentProposalFragment;
  usersForMentions: ReturnType<typeof useGetUsersForMentions>;
}) => {
  const {
    focusThreadId: threadIdFromQueryParams,
    focusCommentId: commentIdFromQueryParams,
  } = useRouteParams(proposalHomepageRouteConfig);
  const [shouldShowEditMode, setShouldShowEditMode] = useState(false);
  const [shouldShowAllReplies, setShouldShowAllReplies] = useState(
    !!commentIdFromQueryParams,
  );
  const [shouldShowReplyEditor, setShouldShowReplyEditor] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [editErrorMessage, setEditErrorMessage] = useState<string>();
  const [shouldShowHighlight, setShouldShowHighlight] = useState(false);
  const { me } = useIdentity();
  const history = useHistory();
  const location = useLocation();

  const {
    addComment,
    loading: addCommentLoading,
    commentErrorMessage,
    setCommentErrorMessage,
    handleAddCommentResult,
  } = useAddProposalComment({
    graphRef,
    proposal,
    users: usersForMentions.users,
    commentThreadId: comment.id,
  });

  const handleSubmitComment = useCallback(
    async (getValue: () => string, setValue: (value: string) => void) => {
      const value = getValue();

      const [result] = await Promise.all([
        addComment({
          message: value,
          ...(comment.__typename === 'ParentChangeProposalComment'
            ? {
                schemaCoordinate: comment.schemaCoordinate,
                schemaScope: comment.schemaScope ?? API_SCHEMA_ID,
              }
            : {}),
        }),
        setValue(''),
        setShouldShowAllReplies(true),
      ]);

      handleAddCommentResult({
        result,
        onError: () => {
          setValue(value);
        },
        onSuccess: () => {
          setShouldShowReplyEditor(false);
        },
      });
    },
    [addComment, handleAddCommentResult, comment],
  );

  // We should clear comment error visibility when user closes reply editor
  useEffect(() => {
    if (!shouldShowReplyEditor) {
      setCommentErrorMessage(null);
    }
  }, [setCommentErrorMessage, shouldShowReplyEditor]);

  const { editComment } = useEditProposalComment({
    proposalId: proposal.id,
    commentId: comment.id,
  });

  const handleEditComment = useCallback(
    async (getValue: () => string) => {
      const value = getValue();

      if (value.trim().length === 0) {
        // open up modal
        history.push(
          setOverlayVisibleLocation({
            location,
            additionalSearchParameters: {
              proposalCommentId: comment.id,
              commentThreadId: comment.id,
            },
            overlayName: Config.modals.deleteProposalComment,
          }),
        );
        setShouldShowEditMode(false);
        setEditErrorMessage(undefined);
        return;
      }
      setIsSubmittingEdit(true);
      const { data: responseData } = await editComment(value);
      setIsSubmittingEdit(false);
      if (
        responseData?.proposal.__typename === 'ProposalMutation' &&
        (responseData.proposal.editComment.__typename ===
          'ParentChangeProposalComment' ||
          responseData.proposal.editComment.__typename ===
            'ReplyChangeProposalComment' ||
          responseData.proposal.editComment.__typename ===
            'ParentGeneralProposalComment' ||
          responseData.proposal.editComment.__typename ===
            'ReplyGeneralProposalComment')
      ) {
        setShouldShowEditMode(false);
        setEditErrorMessage(undefined);
      } else {
        setEditErrorMessage('An error has occurred. Please try again.');
      }
    },
    [editComment, history, location, comment.id],
  );

  useEffect(() => {
    if (threadIdFromQueryParams) {
      setShouldShowHighlight(true);
      if (!commentIdFromQueryParams) {
        scrollTo(`#comment-${threadIdFromQueryParams}`);
      } else {
        scrollTo(`#comment-${commentIdFromQueryParams}`);
      }
      const timeout = setTimeout(() => {
        setShouldShowHighlight(false);
      }, 5_000);
      return () => clearTimeout(timeout);
    }
  }, [commentIdFromQueryParams, threadIdFromQueryParams]);

  const copyCommentLink = useCallback(() => {
    const { pathname = '', search = '' } =
      proposalHomepageRouteConfig.locationFrom({
        location,
        patch: {
          focusThreadId: comment.id,
          focusCommentId: RouteConfig.DELETE,
        },
      });
    copy(`${window.location.origin}${pathname}?${search}`);
  }, [comment.id, location]);

  const shouldShowActionsMenu = comment.status !== CommentStatus.DELETED;
  const isCommentAuthor = me?.id === comment.createdBy?.id;

  return (
    <Card
      isDivided
      className={classnames(
        className,
        comment.id === threadIdFromQueryParams && 'overflow-hidden',
      )}
    >
      <CardHeader>
        <Group>
          {comment.createdBy?.__typename === 'User' ? (
            <Avatar
              size="sm"
              name={comment.createdBy.fullName}
              src={comment.createdBy.avatarUrl ?? undefined}
            />
          ) : (
            <Avatar size="sm" />
          )}
          <CardDescription>
            <Text as="span" variant="bodySb">
              {comment.createdBy?.__typename === 'User'
                ? comment.createdBy.fullName
                : 'Unknown'}
            </Text>{' '}
            {comment.__typename === 'ParentGeneralProposalComment' ||
            comment.__typename === 'ReviewProposalComment' ? (
              'added a comment'
            ) : (
              <>
                commented on{' '}
                {!comment.schemaScope ||
                comment.schemaScope === API_SCHEMA_ID ||
                comment.schemaScope === SUPERGRAPH_SCHEMA_ID ? (
                  <IconServices className="text-icon-primary" />
                ) : (
                  <IconSingleService className="text-icon-primary" />
                )}{' '}
                <Text as="span" variant="bodySb" className="text-neutral">
                  {!comment.schemaScope || comment.schemaScope === API_SCHEMA_ID
                    ? 'API schema'
                    : comment.schemaScope === SUPERGRAPH_SCHEMA_ID
                      ? 'Supergraph schema'
                      : comment.schemaScope}
                </Text>
              </>
            )}
          </CardDescription>
          {shouldShowActionsMenu && (
            <Menu isLazy placement="bottom-start">
              <MenuButton
                as={IconButton}
                size="sm"
                variant="hidden"
                icon={<IconOverflow />}
              />
              <MenuContent>
                <MenuList>
                  {isCommentAuthor && (
                    <>
                      <MenuItem onClick={() => setShouldShowEditMode(true)}>
                        Edit Comment
                      </MenuItem>
                      <MenuItem
                        className="text-error hover:bg-error hover:text-error"
                        as={ReactRouterLink}
                        to={setOverlayVisibleLocation({
                          location,
                          additionalSearchParameters: {
                            proposalCommentId: comment.id,
                            commentThreadId: comment.id,
                          },
                          overlayName: Config.modals.deleteProposalComment,
                        })}
                      >
                        Delete Comment
                      </MenuItem>
                    </>
                  )}
                  <MenuItem onClick={copyCommentLink}>
                    Copy link to comment
                  </MenuItem>
                </MenuList>
              </MenuContent>
            </Menu>
          )}
        </Group>
        <CardHeaderControl>
          <RelativeTimestamp
            value={comment.createdAt}
            className="align-middle text-secondary max-md:hidden"
          />
          <LocalTimestamp
            value={comment.createdAt}
            className="rounded p-2 hover:bg-secondary md:hidden"
          >
            <IconTime className="block size-4" />
          </LocalTimestamp>
        </CardHeaderControl>
      </CardHeader>
      <CardBody>
        {comment.__typename === 'ParentChangeProposalComment' &&
          comment.outdated && (
            <Alert status="warning">
              This conversation is attached to a part of the schema that no
              longer exists.
            </Alert>
          )}
        {comment.__typename === 'ParentChangeProposalComment' && (
          <div className="w-full rounded bg-secondary p-4">
            <div
              className="truncate border-l-2 border-primary pl-3 font-mono text-sm"
              title={comment.schemaCoordinate}
            >
              {comment.schemaCoordinate}
            </div>
          </div>
        )}
        <div>
          {shouldShowEditMode ? (
            <>
              {editErrorMessage && <ErrorMessage className="mt-2" />}
              <CommentMarkdownEditor
                usersForMentions={usersForMentions}
                defaultValue={comment.message}
                className="mt-2"
                shouldShowPlaceholderText={false}
                shouldAutoFocusEditor
                actions={({ hasChanged, getValue }) => {
                  return (
                    <ButtonGroup>
                      <Button
                        variant="secondary"
                        className="w-fit min-w-0"
                        size="sm"
                        onClick={() => {
                          setShouldShowEditMode(false);
                          setEditErrorMessage(undefined);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        className="w-fit min-w-0"
                        isDisabled={!hasChanged}
                        size="sm"
                        isLoading={isSubmittingEdit}
                        loadingText="Save"
                        onClick={() => handleEditComment(getValue)}
                      >
                        Save
                      </Button>
                    </ButtonGroup>
                  );
                }}
              />
            </>
          ) : (
            <SingleProposalComment
              usersForMentions={usersForMentions}
              comment={comment}
              commentThreadId={comment.id}
              proposalId={proposal.id}
              shouldShowActions={false}
              shouldShowAuthor={false}
              shouldHighlight={
                shouldShowHighlight &&
                comment.id === threadIdFromQueryParams &&
                !commentIdFromQueryParams
              }
            />
          )}
        </div>
        {comment.__typename !== 'ReviewProposalComment' &&
          comment.replyCount > 0 && (
            <div className="relative space-y-4 pl-4 before:absolute before:inset-y-0 before:left-0 before:w-px before:border-l before:border-l-primary before:content-['']">
              {comment.replies
                .slice(
                  0,
                  shouldShowAllReplies
                    ? comment.replies.length
                    : MAX_VISIBLE_HOMEPAGE_COMMENTS,
                )
                .map((reply) => (
                  <SingleProposalComment
                    key={reply.id}
                    usersForMentions={usersForMentions}
                    comment={reply}
                    commentThreadId={comment.id}
                    proposalId={proposal.id}
                    loading={reply.id === NEW_COMMENT_TEMP_ID}
                    shouldHighlight={
                      shouldShowHighlight &&
                      reply.id === commentIdFromQueryParams &&
                      reply.id !== NEW_COMMENT_TEMP_ID
                    }
                  />
                ))}

              {comment.replyCount > MAX_VISIBLE_HOMEPAGE_COMMENTS && (
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <AvatarGroup>
                      {!shouldShowAllReplies &&
                        uniqBy(
                          comment.replies
                            .slice(MAX_VISIBLE_HOMEPAGE_COMMENTS)
                            .map(({ createdBy }) => createdBy),
                          (value) => value?.id,
                        ).map((user) => (
                          <Avatar
                            key={user?.id}
                            size="sm"
                            {...(user?.__typename === 'User'
                              ? {
                                  name: user.fullName,
                                  src: user.avatarUrl ?? undefined,
                                }
                              : {})}
                          />
                        ))}
                    </AvatarGroup>
                  </div>
                  <Button
                    variant="buttonLink"
                    onClick={() => setShouldShowAllReplies((value) => !value)}
                  >
                    {shouldShowAllReplies ? (
                      'Collapse replies'
                    ) : (
                      <>
                        Show{' '}
                        {comment.replies.length - MAX_VISIBLE_HOMEPAGE_COMMENTS}{' '}
                        more{' '}
                        {format.pluralize(
                          comment.replies.length -
                            MAX_VISIBLE_HOMEPAGE_COMMENTS,
                          'comment',
                        )}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        {comment.__typename === 'ParentChangeProposalComment' &&
          !comment.outdated && (
            <div>
              <Link
                to={proposalChangesRouteConfig.locationFrom({
                  location,
                  patch: {
                    selectedSchema: comment.schemaScope ?? API_SCHEMA_ID,
                    // We don't know the group name to send the user to but we
                    // need this to open the sidebar for the user. This just
                    // means that we cannot properly scroll to the context
                    // in the changes page
                    linkedGroupName: 'Unknown',
                    linkedCoordinateName: comment.schemaCoordinate,
                    linkedCommentId: comment.id,
                  },
                  fromRouteConfig: proposalHomepageRouteConfig,
                })}
              >
                View discussion in Changes
              </Link>
            </div>
          )}
        {comment.__typename !== 'ReviewProposalComment' && (
          <div>
            {shouldShowReplyEditor ? (
              <>
                {commentErrorMessage !== null && (
                  <ErrorMessage className="mb-4">
                    {commentErrorMessage}
                  </ErrorMessage>
                )}
                <CommentMarkdownEditor
                  usersForMentions={usersForMentions}
                  shouldAutoFocusEditor
                  actions={({ hasComment, getValue, setValue }) => {
                    return (
                      <ButtonGroup>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setShouldShowReplyEditor(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          isLoading={addCommentLoading}
                          loadingText="Comment"
                          isDisabled={!hasComment}
                          size="sm"
                          onClick={() =>
                            handleSubmitComment(getValue, setValue)
                          }
                        >
                          Comment
                        </Button>
                      </ButtonGroup>
                    );
                  }}
                />
              </>
            ) : (
              <PlainHtmlButton
                className="focus-visible:focus-ring w-full cursor-text rounded border border-primary px-4 py-2 text-left text-placeholder"
                onClick={() => setShouldShowReplyEditor(true)}
              >
                Add a reply
              </PlainHtmlButton>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};
